import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import BackendSyncService from '@/service/backendSyncService';
import { SYNC_PROFILES } from '@/service/backendSyncProfiles';
import { resolveSyncTransport } from '@/service/resolveSyncTransport';
import { DELIVERY_STATUS } from '@/constants/deliveryStatus';
import { reservationRepository } from '@/service/repository/reservationRepository';
let weHostScanUi = null;
function registerWeHostScanUi(ui) {
    weHostScanUi = ui;
}
/** Llamado desde `cameraService.handleCheckCodeResponseFromWeHost` (result === 0). */
export function weHostShowReservationSuccess(user, firstReservation) {
    if (!weHostScanUi)
        return;
    weHostScanUi.closeAllResultOverlays();
    weHostScanUi.closeScanner();
    weHostScanUi.setScanData(user ?? {}, firstReservation ?? {});
    weHostScanUi.showSuccessOverlay();
}
/** Llamado desde `cameraService.handleCheckCodeResponseFromWeHost` (result === 1). */
export function weHostShowNoReservation(user) {
    if (!weHostScanUi)
        return;
    weHostScanUi.closeAllResultOverlays();
    weHostScanUi.closeScanner();
    weHostScanUi.setScanData(user ?? {}, {});
    weHostScanUi.showNoReservationOverlay();
}
/** Llamado desde `cameraService.handleCheckCodeResponseFromWeHost` (result === 2). */
export function weHostShowWrongDayReservation(user, firstReservation) {
    if (!weHostScanUi)
        return;
    weHostScanUi.closeAllResultOverlays();
    weHostScanUi.closeScanner();
    weHostScanUi.setScanData(user ?? {}, firstReservation ?? {});
    weHostScanUi.showWrongDayOverlay();
}
/** Llamado desde `cameraService.handleCheckCodeResponseFromWeHost` (result === -1). */
export function weHostShowScanError() {
    if (!weHostScanUi)
        return;
    weHostScanUi.closeAllResultOverlays();
    weHostScanUi.closeScanner();
    weHostScanUi.setScanData({}, {});
    weHostScanUi.showErrorOverlay();
}
export function useWeHost() {
    const store = useStore();
    const instance = getCurrentInstance();
    const cameraService = inject('cameraService');
    const showModalWebCamQRScaner = ref(false);
    const showWalkin = ref(false);
    const showWalkinSuccess = ref(false);
    const showDeliveryOrders = ref(false);
    const showDeliveryPickupNotified = ref(false);
    const walkinResult = ref(null);
    const showSuccess = ref(false);
    const showNoReservation = ref(false);
    const showWrongDay = ref(false);
    const showError = ref(false);
    const businessName = computed(() => store.getters['venue/businessName']);
    const resourceId = computed(() => store.getters['venue/resourceId']);
    const venueId = computed(() => store.getters['venue/venueId']);
    const urlVenue = computed(() => store.getters['venue/urlVenue']);
    const getLinkMenuQr = computed(() => `${urlVenue.value ?? ''}/menu`);
    const scanUser = ref({});
    const scanReservation = ref({});
    function handleShowModalScanQRWebCam() {
        showModalWebCamQRScaner.value = true;
        cameraService?.startCamera();
    }
    function hideShowModalScanQRWebCam() {
        showModalWebCamQRScaner.value = false;
    }
    function closeAllResultOverlays() {
        showSuccess.value = false;
        showNoReservation.value = false;
        showWrongDay.value = false;
        showError.value = false;
        scanUser.value = {};
        scanReservation.value = {};
    }
    function handleRetry() {
        closeAllResultOverlays();
        handleShowModalScanQRWebCam();
    }
    function handleRequestHelp() {
        closeAllResultOverlays();
    }
    function handleWalkinCreated(result) {
        walkinResult.value = result;
        showWalkinSuccess.value = true;
    }
    function closeWalkinSuccess() {
        showWalkinSuccess.value = false;
        walkinResult.value = null;
    }
    async function handleDeliveryOrderSelected(reservation) {
        const deliveryStatus = reservation.deliveryInfo?.deliveryStatus;
        const reservationId = reservation.realId || reservation.id;
        if (reservationId && deliveryStatus === DELIVERY_STATUS.READY_FOR_PICKUP) {
            try {
                await reservationRepository.updateDeliveryStatus(String(reservationId), DELIVERY_STATUS.PICKED_UP);
                await loadCopilotReservations();
            }
            catch (e) {
                console.error('[useWeHost] Error al notificar recogida delivery:', e);
            }
        }
        showDeliveryOrders.value = false;
        showDeliveryPickupNotified.value = true;
    }
    function closeDeliveryPickupNotified() {
        showDeliveryPickupNotified.value = false;
    }
    async function loadCopilotReservations() {
        await store.dispatch('copilot/updateReservations');
    }
    registerWeHostScanUi({
        closeScanner: () => {
            showModalWebCamQRScaner.value = false;
        },
        closeAllResultOverlays,
        setScanData: (user, reservation) => {
            scanUser.value = user;
            scanReservation.value = reservation;
        },
        showSuccessOverlay: () => {
            showSuccess.value = true;
        },
        showNoReservationOverlay: () => {
            showNoReservation.value = true;
        },
        showWrongDayOverlay: () => {
            showWrongDay.value = true;
        },
        showErrorOverlay: () => {
            showError.value = true;
        },
    });
    onMounted(async () => {
        await loadCopilotReservations();
        await BackendSyncService.connect({
            profile: SYNC_PROFILES.WEHOST,
            transport: resolveSyncTransport(),
            context: {
                venueId: venueId.value,
            },
            componentRef: instance?.proxy ?? null,
        });
    });
    watch(showDeliveryOrders, (visible) => {
        if (visible) {
            loadCopilotReservations();
        }
    });
    onBeforeUnmount(() => {
        registerWeHostScanUi(null);
        BackendSyncService.disconnect();
    });
    return {
        showModalWebCamQRScaner,
        showWalkin,
        showWalkinSuccess,
        showDeliveryOrders,
        showDeliveryPickupNotified,
        walkinResult,
        showSuccess,
        showNoReservation,
        showWrongDay,
        showError,
        businessName,
        resourceId,
        venueId,
        urlVenue,
        getLinkMenuQr,
        scanUser,
        scanReservation,
        handleShowModalScanQRWebCam,
        hideShowModalScanQRWebCam,
        closeAllResultOverlays,
        handleRetry,
        handleRequestHelp,
        handleWalkinCreated,
        closeWalkinSuccess,
        handleDeliveryOrderSelected,
        closeDeliveryPickupNotified,
    };
}
